import type { ReactNode } from 'react'
import { useFieldContext, useFieldValue, useFormReadOnly, useIsFieldOptional } from './shared'
import { ImageUpload } from './-components/image-upload'
import { FieldLabelRow } from './field-label-row'
import { useLingui } from '@lingui/react/macro'

/** Value states: `undefined` = keep the existing server image, `File` = new upload, `''` = removed. */
export default function ImageUploadField(props: {
  currentUrl: string | undefined
  buttonLabel: string
  label?: string
  description?: ReactNode
  className?: string
  contain?: boolean
  enableOpenInTab?: boolean
  enableCrop?: boolean
  aspectRatio?: { width: number; height: number }
  size?: 'default' | 'compact'
  noImageText?: string
  openInNewTabText?: string
  cropImageText?: string
  removeImageText?: string
  dropZoneDescription?: string
  orientation?: 'vertical' | 'horizontal'
  children?: ReactNode
}) {
  const { t } = useLingui()
  const field = useFieldContext<File | '' | undefined>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name)
  const value = useFieldValue(field)
  const isRemoved = value === ''
  const isHorizontal = props.orientation === 'horizontal'

  const width = props.aspectRatio?.width
  const height = props.aspectRatio?.height

  return (
    <div className={isHorizontal ? 'flex min-w-0 flex-1 items-start gap-3' : 'min-w-0 flex-1'}>
      {props.label &&
        (isHorizontal ? (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional
            info={props.description}
            className="h-8 w-48 shrink-0"
          />
        ) : (
          <div className="mb-2">
            <FieldLabelRow
              label={props.label}
              htmlFor={field.name}
              hasError={field.state.meta.isTouched && !field.state.meta.isValid}
              optional={isOptional}
              info={props.description}
            />
          </div>
        ))}
      <div className={isHorizontal ? 'min-w-0 flex-1' : undefined}>
        <ImageUpload
          id={field.name}
          currentUrl={isRemoved ? undefined : props.currentUrl}
          file={value instanceof File ? value : undefined}
          onChange={(file) => {
            if (file) field.handleChange(file)
          }}
          onRemove={() => field.handleChange('')}
          label={props.buttonLabel}
          readOnly={formReadOnly}
          contain={props.contain}
          enableOpenInTab={props.enableOpenInTab}
          enableCrop={props.enableCrop}
          aspectRatio={props.aspectRatio}
          size={props.size}
          noImageText={t`No image uploaded`}
          openInNewTabText={t`Open in new tab`}
          cropImageText={t`Crop image`}
          removeImageText={t`Remove image`}
          dropZoneDescription={t`Drag and drop the file here, or upload from your computer`}
          className={props.className}
          imageEditorTexts={{
            title: t`Edit Image`,
            description: props.aspectRatio
              ? t`Drag the corners and edges to crop the image. Aspect ratio is locked to ${width}:${height}. Drag the center to move the crop area.`
              : t`Drag the corners and edges to crop the image. Drag the center to move the crop area.`,
            cancel: t`Cancel`,
            save: t`Save`,
            uploading: t`Uploading...`,
          }}
        >
          {props.children}
        </ImageUpload>
      </div>
    </div>
  )
}
